import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Zap, FileText, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { useAppStore } from '../store';
import { PartInput, selectTemplate, matchMachines, MATERIALS } from '../engineeringRules';

const THREAD_SYSTEM_OPTIONS = ['METRIC', 'INCH'];
const HEAD_TYPES = ['HEX_SOCKET','EXTERNAL_HEX','HEX_FLANGE','PLAIN_HEX','COUNTERSUNK','DRAIN_PLUG','NONE'];
const PART_TYPES = ['BOLT','NUT'];
const MATERIAL_IDS = Object.keys(MATERIALS);

export default function NewJob() {
  const navigate = useNavigate();
  const addJob = useAppStore(s => s.addJob);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const [form, setForm] = useState({
    customer: '',
    part_no: '',
    description: '',
    part_type: 'BOLT' as 'BOLT' | 'NUT',
    nominal_diameter_mm: '',
    thread_pitch_mm: '',
    total_length_mm: '',
    shank_length_mm: '',
    head_type: 'HEX_SOCKET',
    socket_type: 'HEX_SOCKET',
    has_flange: false,
    has_serration: false,
    has_nylon_insert: false,
    nut_height_mm: '',
    nut_af_mm: '',
    wire_diameter_mm: '',
    blank_length_mm: '',
    weight_grams: '',
    material_id: 'SAE1010',
    thread_system: 'METRIC',
    engineer_notes: '',
  });

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.type === 'application/pdf' || f.type.startsWith('image/'))) setFile(f);
  }, []);

  const parseWithAI = async () => {
    if (!file) return;
    setParsing(true); setParseError(null);
    try {
      // AI parse via Anthropic API
      const reader = new FileReader();
      const base64 = await new Promise<string>((res, rej) => {
        reader.onload = () => res((reader.result as string).split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });

      const isImage = file.type.startsWith('image/');
      const mediaType = isImage ? file.type : 'application/pdf';

      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{
            role: 'user',
            content: [
              {
                type: isImage ? 'image' : 'document',
                source: { type: 'base64', media_type: mediaType, data: base64 }
              },
              {
                type: 'text',
                text: `You are ForgeIQ's drawing parser for cold forging at Sturdfix, Ludhiana. 
Extract ALL available fields from this engineering drawing and respond ONLY with a JSON object (no markdown, no preamble):
{
  "part_no": "", "customer": "", "description": "",
  "part_type": "BOLT or NUT",
  "nominal_diameter_mm": 0, "thread_pitch_mm": 0,
  "total_length_mm": 0, "shank_length_mm": 0,
  "head_type": "HEX_SOCKET|EXTERNAL_HEX|HEX_FLANGE|PLAIN_HEX|COUNTERSUNK|DRAIN_PLUG|NONE",
  "socket_type": "HEX_SOCKET|NONE",
  "has_flange": false, "has_serration": false, "has_nylon_insert": false,
  "nut_height_mm": 0, "nut_af_mm": 0,
  "wire_diameter_mm": 0, "blank_length_mm": 0, "weight_grams": 0,
  "material_id": "10B30|10B33|10B21|15B25|SAE1008|SAE1010|SAE1022|SAE1035|SCM435|EN8D|A194_2H",
  "thread_system": "METRIC|INCH",
  "confidence": 0.0 to 1.0,
  "parse_notes": ["any caveats or assumptions made"]
}
Use null for missing fields. For inch drawings, convert dims to mm. Match material to closest known grade.`
              }
            ]
          }]
        })
      });

      if (!resp.ok) throw new Error(`API error ${resp.status}`);
      const data = await resp.json();
      const text = data.content.map((c: any) => c.text || '').join('');
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      // Populate form
      if (parsed.part_no) set('part_no', parsed.part_no);
      if (parsed.customer) set('customer', parsed.customer);
      if (parsed.description) set('description', parsed.description);
      if (parsed.part_type) set('part_type', parsed.part_type);
      if (parsed.nominal_diameter_mm) set('nominal_diameter_mm', String(parsed.nominal_diameter_mm));
      if (parsed.thread_pitch_mm) set('thread_pitch_mm', String(parsed.thread_pitch_mm));
      if (parsed.total_length_mm) set('total_length_mm', String(parsed.total_length_mm));
      if (parsed.shank_length_mm) set('shank_length_mm', String(parsed.shank_length_mm));
      if (parsed.head_type) set('head_type', parsed.head_type);
      if (parsed.socket_type) set('socket_type', parsed.socket_type);
      if (parsed.has_flange !== undefined) set('has_flange', parsed.has_flange);
      if (parsed.has_serration !== undefined) set('has_serration', parsed.has_serration);
      if (parsed.has_nylon_insert !== undefined) set('has_nylon_insert', parsed.has_nylon_insert);
      if (parsed.nut_height_mm) set('nut_height_mm', String(parsed.nut_height_mm));
      if (parsed.nut_af_mm) set('nut_af_mm', String(parsed.nut_af_mm));
      if (parsed.wire_diameter_mm) set('wire_diameter_mm', String(parsed.wire_diameter_mm));
      if (parsed.blank_length_mm) set('blank_length_mm', String(parsed.blank_length_mm));
      if (parsed.weight_grams) set('weight_grams', String(parsed.weight_grams));
      if (parsed.material_id) set('material_id', parsed.material_id);
      if (parsed.thread_system) set('thread_system', parsed.thread_system);

      setStep(2);
    } catch (e: any) {
      setParseError(e.message || 'Parse failed. Please fill in manually.');
      setStep(2);
    } finally {
      setParsing(false);
    }
  };

  const buildPartInput = (): PartInput => ({
    part_type: form.part_type,
    nominal_diameter_mm: parseFloat(form.nominal_diameter_mm) || 10,
    thread_pitch_mm: parseFloat(form.thread_pitch_mm) || 1.5,
    total_length_mm: parseFloat(form.total_length_mm) || 30,
    shank_length_mm: parseFloat(form.shank_length_mm) || undefined,
    head_type: form.head_type as any,
    socket_type: form.socket_type as any,
    has_flange: form.has_flange,
    has_serration: form.has_serration,
    has_nylon_insert: form.has_nylon_insert,
    nut_height_mm: parseFloat(form.nut_height_mm) || undefined,
    nut_af_mm: parseFloat(form.nut_af_mm) || undefined,
    wire_diameter_mm: parseFloat(form.wire_diameter_mm) || undefined,
    blank_length_mm: parseFloat(form.blank_length_mm) || undefined,
    weight_grams: parseFloat(form.weight_grams) || undefined,
    material_id: form.material_id,
    thread_system: form.thread_system as any,
  });

  const handleCreate = () => {
    const part = buildPartInput();
    const { template } = selectTemplate(part);
    const id = addJob({
      status: 'REVIEW',
      customer: form.customer,
      part_no: form.part_no,
      description: form.description,
      drawing_filename: file?.name,
      part,
      engineer_notes: form.engineer_notes,
    });
    navigate(`/jobs/${id}`);
  };

  const templatePreview = form.nominal_diameter_mm ? (() => {
    const p = buildPartInput();
    const { template, confidence, reasoning } = selectTemplate(p);
    const machines = matchMachines(p, template);
    const eligible = machines.filter(m => m.eligible);
    return { template, confidence, reasoning, eligible };
  })() : null;

  return (
    <div className="fade-in" style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 24 }}>
        <h1>New Job</h1>
        <p style={{ color: 'var(--forge-text2)', fontSize: 12, marginTop: 4 }}>Upload a drawing or enter dimensions manually</p>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        {[{n:1,l:'Upload'},{n:2,l:'Dimensions'},{n:3,l:'Confirm'}].map(({n,l}) => (
          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%',
              background: step >= n ? 'var(--forge-accent)' : 'var(--forge-surface3)',
              color: step >= n ? '#0a0b0d' : 'var(--forge-text3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600,
            }}>{step > n ? '✓' : n}</div>
            <span style={{ fontSize: 11, color: step >= n ? 'var(--forge-text)' : 'var(--forge-text3)', fontWeight: step === n ? 500 : 400 }}>{l}</span>
            {n < 3 && <div style={{ width: 24, height: 1, background: 'var(--forge-border)' }} />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Upload Drawing</h3>

          <div
            className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input id="file-input" type="file" accept=".pdf,image/*" style={{ display:'none' }}
              onChange={e => e.target.files?.[0] && setFile(e.target.files[0])} />
            {file ? (
              <div>
                <CheckCircle size={32} color="var(--forge-green)" style={{ margin: '0 auto 8px' }} />
                <p style={{ color: 'var(--forge-text)', fontWeight: 500 }}>{file.name}</p>
                <p style={{ color: 'var(--forge-text3)', fontSize: 11, marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB</p>
              </div>
            ) : (
              <div>
                <Upload size={32} color="var(--forge-text3)" style={{ margin: '0 auto 10px' }} />
                <p style={{ color: 'var(--forge-text2)' }}>Drop PDF or image here</p>
                <p style={{ color: 'var(--forge-text3)', fontSize: 11, marginTop: 4 }}>or click to browse</p>
              </div>
            )}
          </div>

          {parseError && (
            <div className="alert alert-error" style={{ marginTop: 12 }}>
              <AlertCircle size={14} /> {parseError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {file && !parsing && (
              <button className="btn btn-primary" onClick={parseWithAI}>
                <Zap size={13} /> Parse with AI
              </button>
            )}
            {parsing && (
              <button className="btn btn-primary" disabled>
                <span className="spinner" style={{ width: 12, height: 12 }} /> Parsing drawing…
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => setStep(2)}>
              Enter manually
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Dimensions form */}
      {step === 2 && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Part Dimensions</h3>

          <div className="form-row" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label>Customer</label>
              <input className="input" value={form.customer} onChange={e => set('customer', e.target.value)} placeholder="Customer name" />
            </div>
            <div className="form-group">
              <label>Part No.</label>
              <input className="input" value={form.part_no} onChange={e => set('part_no', e.target.value)} placeholder="Customer part number" />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Description</label>
            <input className="input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Hex Flange Bolt M10×1.5×25 Grade 8.8" />
          </div>

          <hr className="divider" />

          <div className="form-row" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label>Part Type</label>
              <select className="select" value={form.part_type} onChange={e => set('part_type', e.target.value)}>
                {PART_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Thread System</label>
              <select className="select" value={form.thread_system} onChange={e => set('thread_system', e.target.value)}>
                {THREAD_SYSTEM_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label>Nominal Ø (mm)</label>
              <input className="input" type="number" value={form.nominal_diameter_mm} onChange={e => set('nominal_diameter_mm', e.target.value)} placeholder="10" />
            </div>
            <div className="form-group">
              <label>Pitch (mm)</label>
              <input className="input" type="number" value={form.thread_pitch_mm} onChange={e => set('thread_pitch_mm', e.target.value)} placeholder="1.50" />
            </div>
            <div className="form-group">
              <label>Total Length (mm)</label>
              <input className="input" type="number" value={form.total_length_mm} onChange={e => set('total_length_mm', e.target.value)} placeholder="25" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Wire Ø (mm)</label>
              <input className="input" type="number" value={form.wire_diameter_mm} onChange={e => set('wire_diameter_mm', e.target.value)} placeholder="Auto-calc if blank" />
            </div>
            <div className="form-group">
              <label>Blank Length (mm)</label>
              <input className="input" type="number" value={form.blank_length_mm} onChange={e => set('blank_length_mm', e.target.value)} placeholder="Auto-calc if blank" />
            </div>
          </div>

          {form.part_type === 'BOLT' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Head Type</label>
                  <select className="select" value={form.head_type} onChange={e => set('head_type', e.target.value)}>
                    {HEAD_TYPES.map(h => <option key={h} value={h}>{h.replace(/_/g,' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Shank Length (mm)</label>
                  <input className="input" type="number" value={form.shank_length_mm} onChange={e => set('shank_length_mm', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
                {[
                  { key: 'has_flange', label: 'Has Flange' },
                  { key: 'has_serration', label: 'Has Serration' },
                ].map(({ key, label }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', textTransform: 'none', fontSize: 12, color: 'var(--forge-text2)', marginBottom: 0 }}>
                    <input type="checkbox" checked={(form as any)[key]} onChange={e => set(key, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
            </>
          )}

          {form.part_type === 'NUT' && (
            <div className="form-row">
              <div className="form-group">
                <label>Nut Height (mm)</label>
                <input className="input" type="number" value={form.nut_height_mm} onChange={e => set('nut_height_mm', e.target.value)} />
              </div>
              <div className="form-group">
                <label>A/F (mm)</label>
                <input className="input" type="number" value={form.nut_af_mm} onChange={e => set('nut_af_mm', e.target.value)} />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Material</label>
              <select className="select" value={form.material_id} onChange={e => set('material_id', e.target.value)}>
                {MATERIAL_IDS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Weight (g)</label>
              <input className="input" type="number" value={form.weight_grams} onChange={e => set('weight_grams', e.target.value)} placeholder="For volume check" />
            </div>
          </div>

          {/* Live template preview */}
          {templatePreview && (
            <div style={{ background: 'var(--forge-surface2)', border: '1px solid var(--forge-border)', borderRadius: 6, padding: 12, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Zap size={12} color="var(--forge-accent)" />
                <span style={{ fontSize: 11, color: 'var(--forge-text2)', fontWeight: 500 }}>AI Template Preview</span>
                <span className={`badge ${templatePreview.confidence > 0.85 ? 'badge-green' : 'badge-yellow'}`}>
                  {(templatePreview.confidence * 100).toFixed(0)}% confidence
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forge-accent)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>
                Template {templatePreview.template}
              </div>
              {templatePreview.reasoning.map((r, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--forge-text3)', marginTop: 2 }}>→ {r}</div>
              ))}
              {templatePreview.eligible.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: 'var(--forge-text3)' }}>Eligible machines:</span>
                  {templatePreview.eligible.slice(0, 3).map(m => (
                    <span key={m.machine_id} className="badge badge-blue">{m.machine_name}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}
              disabled={!form.nominal_diameter_mm || !form.customer}>
              Review → </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (() => {
        const part = buildPartInput();
        const { template, confidence, reasoning } = selectTemplate(part);
        const machines = matchMachines(part, template).filter(m => m.eligible);
        return (
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Confirm Job</h3>

            <div style={{ background: 'var(--forge-surface2)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div className="flex-between" style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--forge-text2)' }}>Part</span>
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--forge-text)' }}>{form.part_no || '—'} · {form.description || '—'}</span>
              </div>
              <div className="flex-between" style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--forge-text2)' }}>Customer</span>
                <span style={{ fontSize: 12 }}>{form.customer}</span>
              </div>
              <div className="flex-between" style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--forge-text2)' }}>Template</span>
                <span className="badge badge-orange">Template {template}</span>
              </div>
              <div className="flex-between">
                <span style={{ fontSize: 12, color: 'var(--forge-text2)' }}>Eligible machines</span>
                <span style={{ fontSize: 12 }}>{machines.length} matched</span>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label>Engineer Notes</label>
              <textarea className="textarea" value={form.engineer_notes}
                onChange={e => set('engineer_notes', e.target.value)}
                placeholder="Any special requirements, customer callouts, or process notes…" />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setStep(2)}>← Back</button>
              <button className="btn btn-primary btn-lg" onClick={handleCreate}>
                <Zap size={14} /> Create Job & Generate Sequence
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
